import { Component, EventEmitter, HostListener, OnDestroy, OnInit, Output } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, filter, takeUntil } from 'rxjs';
import { DataServiceService } from '../data-service.service';
import { resolveAppFormRoute } from '../core/navigation/app-form-route.map';
import { NgxSpinnerService } from 'ngx-spinner';

export interface LayoutMenuItem {
  appFormId: number;
  appFormName: string;
  appFormLabel: string;
  route: string | null;
  addDetails?: boolean;
  updateDetails?: boolean;
}

export interface LayoutMenuGroup {
  formGroup: string;
  formGroupLabel: string;
  expanded: boolean;
  appForms: LayoutMenuItem[];
}

@Component({
  selector: 'app-layout-sidebar',
  templateUrl: './layout-sidebar.component.html',
  styleUrls: ['./layout-sidebar.component.scss'],
})
export class LayoutSidebarComponent implements OnInit, OnDestroy {
  @Output() navigateClose = new EventEmitter<void>();
  /** Ask shell to expand the icon rail (e.g. when opening a group while collapsed). */
  @Output() requestExpand = new EventEmitter<void>();

  menuGroups: LayoutMenuGroup[] = [];
  filteredGroups: LayoutMenuGroup[] = [];
  searchTerm = '';
  loading = false;
  activeRoute = '';
  totalForms = 0;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly dataService: DataServiceService,
    private readonly router: Router,
    private readonly spinner:NgxSpinnerService
  ) {}

  ngOnInit(): void {
    this.activeRoute = this.router.url.split('?')[0];
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((e) => {
        this.activeRoute = e.urlAfterRedirects.split('?')[0];
      });
    this.loadRoleMenu();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadRoleMenu(): void {
    this.loading = true;
    this.dataService.rolemanagementWeb(null, 'WEB').subscribe({
      next: (res: any) => {
        if (!res) {
          this.spinner.hide();
          return;
        }
        const groups = res?.userAppForms ?? res?.data?.userAppForms ?? [];
        this.menuGroups = (Array.isArray(groups) ? groups : []).map((g: any, index: number) => ({
          formGroup: String(g?.formGroup ?? ''),
          formGroupLabel: String(g?.formGroupLabel ?? g?.formGroup ?? 'Module'),
          expanded: index === 0,
          appForms: (Array.isArray(g?.appForms) ? g.appForms : []).map((f: any) => ({
            appFormId: Number(f?.appFormId) || 0,
            appFormName: String(f?.appFormName ?? ''),
            appFormLabel: String(f?.appFormLabel ?? f?.appFormName ?? 'Form'),
            route: resolveAppFormRoute(f?.appFormName),
            addDetails: !!f?.addDetails,
            updateDetails: !!f?.updateDetails,
          })),
        }));
        this.totalForms = this.menuGroups.reduce((n, g) => n + g.appForms.length, 0);
        this.applyFilter();
        this.loading = false;
      },
      error: () => {
        this.menuGroups = [];
        this.filteredGroups = [];
        this.totalForms = 0;
        this.loading = false;
        this.spinner.hide();
      },
    });
  }

  onSearch(value: string): void {
    this.searchTerm = value ?? '';
    this.applyFilter();
  }

  toggleGroup(group: LayoutMenuGroup): void {
    group.expanded = !group.expanded;
  }

  onGroupClick(group: LayoutMenuGroup): void {
    this.requestExpand.emit();
    this.toggleGroup(group);
  }

  isActive(route: string | null): boolean {
    if (!route) {
      return false;
    }
    return this.activeRoute === route || this.activeRoute.startsWith(route + '/');
  }

  isGroupActive(group: LayoutMenuGroup): boolean {
    return group.appForms.some((f) => this.isActive(f.route));
  }

  openForm(item: LayoutMenuItem): void {
    if (!item.route) {
      return;
    }
    this.router.navigateByUrl(item.route);
    this.navigateClose.emit();
  }

  goHome(): void {
    this.router.navigateByUrl('/appScaits/mainDashboard');
    this.navigateClose.emit();
  }

  // iconForGroup(group: LayoutMenuGroup): string {
  //   const key = `${group.formGroup} ${group.formGroupLabel}`.toLowerCase();
  //   if (/mess|meal|inventory|daysheet|food/.test(key)) {
  //     return 'fa-cutlery';
  //   }
  //   if (/hr|hrms|payroll|finance|salary|cost|budget|mrf|money/.test(key)) {
  //     return 'fa-money';
  //   }
  //   if (/academic|counsel|ipe|score|course|exam|mark|education|training/.test(key)) {
  //     return 'fa-graduation-cap';
  //   }
  //   if (/grievance|ticket|feedback|employee|people|communication/.test(key)) {
  //     return 'fa-comments';
  //   }
  //   if (/track|campus|meraki|cctv|dvr|map|outing|sign/.test(key)) {
  //     return 'fa-map-signs';
  //   }
  //   if (/hygiene|hygine|housekeep|building/.test(key)) {
  //     return 'fa-building';
  //   }
  //   if (/attendance/.test(key)) {
  //     return 'fa-calendar-check-o';
  //   }
  //   if (/notification/.test(key)) {
  //     return 'fa-bell';
  //   }
  //   if (/asset|vehicle|qr/.test(key)) {
  //     return 'fa-qrcode';
  //   }
  //   return 'fa-th-large';
  // }

  iconForGroup(group: LayoutMenuGroup): string {
    const key = `${group.formGroup} ${group.formGroupLabel}`.toLowerCase();
    if (/mess|meal|inventory|daysheet|food/.test(key)) {
      return 'fa-cutlery';
    }
    if (/hr|hrms|payroll|finance|salary|cost|budget|mrf|money/.test(key)) {
      return 'fa-clipboard';
    }
    if (/student|scholar|admission/.test(key)) {
      return 'fa-user-graduate';   // student / individual learner
    }
    if (/academic|counsel|ipe|score|course|exam|mark|education|training/.test(key)) {
      return 'fa-graduation-cap';
    }
    if (/surveillance|cctv|dvr|monitor|watch|security cam/.test(key)) {
      return 'fa-video-camera';    // surveillance
    }
    if (/leadership|management|principal|director|head|admin(?!ission)/.test(key)) {
      return 'fa-user-tie';           // leadership
    }
    if (/grievance|ticket|feedback|employee|people|communication/.test(key)) {
      return 'fa-users';
    }
    if (/track|campus|meraki|map|outing|sign/.test(key)) {
      return 'fa-map-signs';
    }
    if (/hygiene|hygine|housekeep|building/.test(key)) {
      return 'fa-building';
    }
      if (/report|analytic|summary|dashboard|stat/.test(key)) {
      return 'fa-bar-chart';   // reports / analytics
    }
    if (/attendance/.test(key)) {
      return 'fa-calendar-check-o';
    }
    if (/notification/.test(key)) {
      return 'fa-bell';
    }
    if (/asset|vehicle|qr/.test(key)) {
      return 'fa-qrcode';
    }
    return 'fa-th-large';
}

  private applyFilter(): void {
    const q = this.searchTerm.trim().toLowerCase();
    if (!q) {
      this.filteredGroups = this.menuGroups;
      return;
    }
    this.filteredGroups = this.menuGroups
      .map((g) => ({
        ...g,
        expanded: true,
        appForms: g.appForms.filter(
          (f) =>
            f.appFormLabel.toLowerCase().includes(q) ||
            f.appFormName.toLowerCase().includes(q) ||
            g.formGroupLabel.toLowerCase().includes(q)
        ),
      }))
      .filter((g) => g.appForms.length > 0);
  }

  @HostListener('window:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.navigateClose.emit();
    }
  }
}
